import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient, useAuth, usePermissions } from "../App";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Clock, 
  Pause, 
  DollarSign, 
  ChevronRight,
  CheckCircle2,
  Plus,
  Trash2,
  Square,
  CheckSquare,
  TrendingDown,
  Wallet,
  Target,
  Calendar,
  Megaphone,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { usePmTestMode, isProgramVisibleToPm } from "../hooks/usePmTestMode";
import { WorldClocks, DynamicGreeting } from "../components/WorldClocks";
import DeadlinesPanel from "../components/today/DeadlinesPanel";
import OperationalFeed from "../components/today/OperationalFeed";
import TrophyCase from "../components/today/TrophyCase";
import FinancialWidget from "../components/today/FinancialWidget";
import Spotlight from "../components/today/Spotlight";

const TodayView = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    message: "",
    target_roles: [],
    priority: "normal"
  });
  const [users, setUsers] = useState([]);
  const [excludedUsers, setExcludedUsers] = useState([]);
  
  // PM Test Mode and financial visibility
  const pmTestMode = usePmTestMode();
  const isPM = user?.role === "Program Manager" || user?.role === "Marketing PM";
  const isArtist = user?.role === "Artist";
  // Check for artist test mode in localStorage (set by Layout)
  const artistTestMode = typeof window !== 'undefined' && localStorage.getItem('artistTestMode') === 'true';
  
  // Use permissions from backend (handles user-specific restrictions)
  const { canViewFinancials: permCanViewFinancials } = usePermissions();
  const canViewFinancials = permCanViewFinancials && !pmTestMode?.isActive && !artistTestMode;
  const isAdmin = user?.role === "COO" || user?.role === "Admin";

  useEffect(() => {
    fetchTodayData();
    fetchTasks();
    fetchAnnouncements();
    if (isAdmin) fetchUsers();
  }, []);
  
  const fetchAnnouncements = async () => {
    try {
      const res = await apiClient.get("/announcements");
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error("Failed to fetch announcements", err);
    }
  };
  
  const fetchUsers = async () => {
    try {
      const res = await apiClient.get("/users");
      setUsers(res.data || []);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };
  
  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.message) {
      toast.error("Title and message are required");
      return;
    }
    try {
      await apiClient.post("/announcements", {
        ...announcementForm,
        excluded_user_ids: excludedUsers
      });
      toast.success("Announcement created and notifications sent");
      setShowAnnouncementDialog(false);
      setAnnouncementForm({ title: "", message: "", target_roles: [], priority: "normal" });
      setExcludedUsers([]);
      fetchAnnouncements();
    } catch (err) {
      toast.error("Failed to create announcement");
    }
  };
  
  const handleDismissAnnouncement = async (announcementId) => {
    try {
      await apiClient.post(`/announcements/${announcementId}/dismiss`);
      setAnnouncements(prev => prev.map(a => 
        a.id === announcementId ? { ...a, is_dismissed: true } : a
      ));
    } catch (err) {
      console.error("Failed to dismiss announcement", err);
    }
  };

  const fetchTodayData = async () => {
    try {
      const response = await apiClient.get("/dashboard/today");
      let dashboardData = response.data;
      
      // Filter data if PM test mode is active
      if (pmTestMode.isActive && pmTestMode.pmId) {
        dashboardData = {
          ...dashboardData,
          at_risk: (dashboardData.at_risk || []).filter(p => isProgramVisibleToPm(p, pmTestMode.pmId, pmTestMode.pmName)),
          stalled: (dashboardData.stalled || []).filter(p => isProgramVisibleToPm(p, pmTestMode.pmId, pmTestMode.pmName)),
          paused: (dashboardData.paused || []).filter(p => isProgramVisibleToPm(p, pmTestMode.pmId, pmTestMode.pmName)),
          urgent: (dashboardData.urgent || []).filter(p => isProgramVisibleToPm(p, pmTestMode.pmId, pmTestMode.pmName)),
          overdue: (dashboardData.overdue || []).filter(p => isProgramVisibleToPm(p, pmTestMode.pmId, pmTestMode.pmName)),
        };
      }
      
      setData(dashboardData);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await apiClient.get("/tasks");
      setTasks(response.data);
    } catch (error) {
      console.error("Failed to load tasks", error);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    
    setAddingTask(true);
    try {
      const response = await apiClient.post("/tasks", { title: newTaskTitle.trim() });
      setTasks(prev => [response.data, ...prev]);
      setNewTaskTitle("");
      toast.success("Task added");
    } catch (error) {
      toast.error("Failed to add task");
    } finally {
      setAddingTask(false);
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      await apiClient.patch(`/tasks/${taskId}`, { completed: !completed });
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, completed: !completed, completed_at: !completed ? new Date().toISOString() : null } : t
      ));
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await apiClient.delete(`/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const incompleteTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const programsAtRisk = data?.programs_at_risk || [];
  const deliverablesdue = data?.deliverables_due_soon || [];
  const stalledItems = data?.stalled_items || [];
  const stalledPrograms = data?.stalled_programs || [];
  const financialAlerts = data?.financial_alerts || [];
  const programsHighExposure = data?.programs_high_exposure || [];
  const upcomingMilestones = data?.upcoming_milestones || [];
  const financials = data?.financials || {};
  const capacity = data?.capacity || {};
  const isCalm = data?.is_calm;

  // Task checklist component (reusable)
  const TaskChecklist = () => (
    <Card className="border border-border" data-testid="daily-tasks">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">My Tasks</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {completedTasks.length > 0 && (
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {showCompleted ? "Hide" : "Show"} completed ({completedTasks.length})
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-1">
        <form onSubmit={handleAddTask} className="flex items-center gap-2">
          <Input
            placeholder="Add a new task..."
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            className="flex-1 h-10"
            data-testid="new-task-input"
          />
          <Button 
            type="submit" 
            size="sm" 
            className="h-10 px-3 rounded-lg"
            disabled={addingTask || !newTaskTitle.trim()}
            data-testid="add-task-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </form>

        {incompleteTasks.length === 0 && completedTasks.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No tasks yet. Add one above to get started.
          </p>
        ) : (
          <div className="space-y-2">
            {incompleteTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 group hover:bg-secondary/50 transition-colors"
                data-testid={`task-${task.id}`}
              >
                <button
                  onClick={() => handleToggleTask(task.id, task.completed)}
                  className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
                  data-testid={`toggle-task-${task.id}`}
                >
                  <Square className="w-5 h-5" />
                </button>
                <span className="flex-1 text-sm">{task.title}</span>
                <button
                  onClick={() => handleDeleteTask(task.id)}
                  className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                  data-testid={`delete-task-${task.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {showCompleted && completedTasks.length > 0 && (
          <div className="space-y-2 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Completed</p>
            {completedTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-green-50/50 group"
                data-testid={`task-${task.id}`}
              >
                <button
                  onClick={() => handleToggleTask(task.id, task.completed)}
                  className="flex-shrink-0 text-green-600 hover:text-green-700 transition-colors"
                  data-testid={`toggle-task-${task.id}`}
                >
                  <CheckSquare className="w-5 h-5" />
                </button>
                <span className="flex-1 text-sm text-muted-foreground line-through">{task.title}</span>
                <button
                  onClick={() => handleDeleteTask(task.id)}
                  className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                  data-testid={`delete-task-${task.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Financial Summary component (reusable)
  const FinancialSummary = () => (
    <Card className="border border-border" data-testid="financial-summary">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Financial Snapshot</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mb-6">
          <div>
            <p className="label-text mb-1">Contracted</p>
            <p className="text-xl md:text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
              {formatCurrency(financials.total_contracted)}
            </p>
          </div>
          <div>
            <p className="label-text mb-1">Collected</p>
            <p className="text-xl md:text-2xl font-light text-green-600" style={{ fontFamily: 'var(--font-sans)' }}>
              {formatCurrency(financials.total_collected)}
            </p>
          </div>
          <div>
            <p className="label-text mb-1">Outstanding</p>
            <p className="text-xl md:text-2xl font-light text-orange-600" style={{ fontFamily: 'var(--font-sans)' }}>
              {formatCurrency(financials.total_outstanding)}
            </p>
          </div>
          <div>
            <p className="label-text mb-1">Collection Rate</p>
            <p className="text-xl md:text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
              {financials.collection_rate || 0}%
            </p>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>Collection Progress</span>
            <span>{formatCurrency(financials.total_collected)} of {formatCurrency(financials.total_contracted)}</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(financials.collection_rate || 0, 100)}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Calm state - nothing needs attention
  if (isCalm) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="today-view">
        <div className="flex items-start justify-between mb-8">
          <div>
            <DynamicGreeting userName={user?.name} />
            <p className="body-text mt-1">What needs your attention</p>
          </div>
          <div className="hidden lg:block">
            <WorldClocks />
          </div>
        </div>

        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" strokeWidth={1.5} />
          </div>
          <h2 className="heading-2 text-foreground mb-2">All Systems Nominal</h2>
          <p className="text-muted-foreground text-sm max-w-md">
            No programs at risk, no deliverables due soon, and no stalled projects. 
            Everything is flowing smoothly.
          </p>
        </div>

        <div className="space-y-6">
          <DeadlinesPanel />
          <OperationalFeed />
          <TrophyCase />
          <Spotlight />
          <TaskChecklist />
          {canViewFinancials && <FinancialWidget />}
          {canViewFinancials && <FinancialSummary />}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="today-view">
      <div className="flex items-start justify-between mb-8">
        <div>
          <DynamicGreeting userName={user?.name} />
          <p className="body-text mt-1">What needs your attention</p>
        </div>
        <div className="hidden lg:block">
          <WorldClocks />
        </div>
      </div>

      <div className="space-y-6">
        {/* Financial Alerts - Hidden for PM and Artist */}
        {canViewFinancials && financialAlerts.length > 0 && (
          <Card className="border-border" data-testid="financial-alerts">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                  <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-amber-600">
                    Financial Alerts
                  </CardTitle>
                </div>
                <Badge className="rounded-full bg-amber-100 text-amber-700 border-amber-200">
                  {financialAlerts.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-1">
              {financialAlerts.map((alert, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 py-3 px-1 border-t first:border-t-0 ${
                    alert.severity === 'critical' ? 'border-terracotta-200' : 'border-border'
                  }`}
                  data-testid={`financial-alert-${index}`}
                >
                  {alert.type === 'low_collection' ? (
                    <TrendingDown className={`w-5 h-5 mt-0.5 ${alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                  ) : (
                    <DollarSign className={`w-5 h-5 mt-0.5 ${alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                  )}
                  <div>
                    <p className="font-medium text-sm">{alert.message}</p>
                    <p className="text-xs text-muted-foreground">{alert.detail}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Programs at Risk */}
        {programsAtRisk.length > 0 && (
          <Card className="border-border" data-testid="programs-at-risk">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                  <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-red-600">
                    Programs at Risk
                  </CardTitle>
                </div>
                <Badge variant="destructive" className="rounded-full">
                  {programsAtRisk.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-1">
              {programsAtRisk.map((program) => (
                <Link
                  key={program.id}
                  to={`/programs/${program.id}`}
                  className="flex items-center justify-between px-1 py-3.5 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
                  data-testid={`risk-program-${program.id}`}
                >
                  <div>
                    <p className="font-medium text-sm">{program.program_name}</p>
                    <p className="text-xs text-muted-foreground">{program.client_name}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Programs with High Exposure - NEW */}
        {programsHighExposure.length > 0 && (
          <Card className="border-border" data-testid="programs-high-exposure">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-purple-600" strokeWidth={1.5} />
                  <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-purple-600">
                    High Exposure Programs
                  </CardTitle>
                </div>
                <Badge className="rounded-full bg-purple-100 text-purple-700 border-purple-200">
                  {programsHighExposure.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-1">
              {programsHighExposure.map((program) => (
                <Link
                  key={program.id}
                  to={`/programs/${program.id}`}
                  className="flex items-center justify-between px-1 py-3.5 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
                  data-testid={`exposure-program-${program.id}`}
                >
                  <div>
                    <p className="font-medium text-sm">{program.program_name}</p>
                    <p className="text-xs text-muted-foreground">{program.client_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {!pmTestMode.isActive && (
                      <div className="text-right">
                        <p className="text-sm font-semibold text-purple-600">{formatCurrency(program.exposure)}</p>
                        <p className="text-[10px] text-muted-foreground">{program.collection_rate}% collected</p>
                      </div>
                    )}
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Deliverables Due Soon */}
        {deliverablesdue.length > 0 && (
          <Card className="border-border" data-testid="deliverables-due">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-600" strokeWidth={1.5} />
                  <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-orange-600">
                    Due in 7 Days
                  </CardTitle>
                </div>
                <Badge className="rounded-full bg-orange-100 text-orange-700 border-orange-200">
                  {deliverablesdue.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-1">
              {deliverablesdue.map((deliv) => (
                <Link
                  key={deliv.id}
                  to={`/programs/${deliv.program_id}`}
                  className="flex items-center justify-between px-1 py-3.5 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
                  data-testid={`due-deliverable-${deliv.id}`}
                >
                  <div>
                    <p className="font-medium text-sm">{deliv.deliverable_type}</p>
                    <p className="text-xs text-muted-foreground">{deliv.program_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-orange-600">
                      {new Date(deliv.due_window).toLocaleDateString()}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Upcoming Milestones - NEW */}
        {upcomingMilestones.length > 0 && (
          <Card className="border-border" data-testid="upcoming-milestones">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
                  <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-blue-600">
                    Upcoming Milestones (14 days)
                  </CardTitle>
                </div>
                <Badge className="rounded-full bg-blue-100 text-blue-700 border-blue-200">
                  {upcomingMilestones.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-1">
              {upcomingMilestones.map((milestone) => (
                <Link
                  key={milestone.id}
                  to={`/programs/${milestone.program_id}`}
                  className="flex items-center justify-between px-1 py-3.5 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
                  data-testid={`milestone-${milestone.id}`}
                >
                  <div>
                    <p className="font-medium text-sm">{milestone.name}</p>
                    <p className="text-xs text-muted-foreground">{milestone.program_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-blue-600 font-medium">
                        {milestone.days_until === 0 ? 'Today' : 
                         milestone.days_until === 1 ? 'Tomorrow' : 
                         `${milestone.days_until} days`}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(milestone.target_date).toLocaleDateString()}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Deadlines Panel - Month-based filtering */}
        <DeadlinesPanel />

        {/* Operational Feed - PTOs, Holidays, Deadlines */}
        <OperationalFeed />

        {/* Trophy Case - Recently Closed Projects */}
        <TrophyCase />

        {/* Spotlight - Recently Published Media */}
        <Spotlight />

        {/* Financial Widget - Enhanced financial overview */}
        {canViewFinancials && <FinancialWidget />}

        {/* Financial Summary - Legacy, keeping for backup */}
        {canViewFinancials && <FinancialSummary />}
        
        {/* Announcements Widget */}
        {announcements.filter(a => !a.is_dismissed).length > 0 && (
          <Card className="border-border" data-testid="announcements-widget">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
                  <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-blue-700">
                    Announcements
                  </CardTitle>
                </div>
                {isAdmin && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 text-xs text-blue-600 dark:text-blue-400"
                    onClick={() => setShowAnnouncementDialog(true)}
                  >
                    <Plus className="w-3 h-3 mr-1" /> New
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-1">
              {announcements.filter(a => !a.is_dismissed).slice(0, 3).map((ann) => (
                <div
                  key={ann.id}
                  className={`py-3 px-1 border-t first:border-t-0 transition-colors ${
                    ann.priority === 'urgent' ? 'border-terracotta-200' : 
                    ann.priority === 'high' ? 'border-warm-300' : 
                    'border-border'
                  }`}
                  data-testid={`announcement-${ann.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm truncate">{ann.title}</p>
                        {ann.priority === 'urgent' && (
                          <Badge className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 text-[10px]">
                            Urgent
                          </Badge>
                        )}
                        {ann.priority === 'high' && (
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 text-[10px]">
                            Important
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{ann.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        — {ann.created_by_name}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => handleDismissAnnouncement(ann.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Admin: Create Announcement Button when no announcements */}
        {isAdmin && announcements.filter(a => !a.is_dismissed).length === 0 && (
          <Card className="border border-dashed border-muted-foreground/30">
            <CardContent className="py-6">
              <div className="flex flex-col items-center text-center">
                <Megaphone className="w-8 h-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground mb-3">No active announcements</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAnnouncementDialog(true)}
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Announcement
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Create Announcement Dialog */}
        <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                Create Announcement
              </DialogTitle>
              <DialogDescription>
                Send an announcement to selected users. They will receive a notification.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="ann-title">Title</Label>
                <Input
                  id="ann-title"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Announcement title"
                />
              </div>
              
              <div>
                <Label htmlFor="ann-message">Message</Label>
                <Textarea
                  id="ann-message"
                  value={announcementForm.message}
                  onChange={(e) => setAnnouncementForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Write your announcement..."
                  rows={4}
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={announcementForm.priority} 
                  onValueChange={(v) => setAnnouncementForm(f => ({ ...f, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Target Roles (empty = all users)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["COO", "Admin", "Program Manager", "Marketing PM", "Artist", "Sales", "Accountant"].map(role => (
                    <label key={role} className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={announcementForm.target_roles.includes(role)}
                        onCheckedChange={(checked) => {
                          setAnnouncementForm(f => ({
                            ...f,
                            target_roles: checked 
                              ? [...f.target_roles, role]
                              : f.target_roles.filter(r => r !== role)
                          }));
                        }}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>
              
              {users.length > 0 && announcementForm.target_roles.length > 0 && (
                <div>
                  <Label>Exclude Specific Users</Label>
                  <div className="max-h-32 overflow-y-auto mt-2 space-y-1 border rounded p-2">
                    {users
                      .filter(u => announcementForm.target_roles.includes(u.role))
                      .map(u => (
                        <label key={u.id} className="flex items-center gap-1.5 text-sm">
                          <Checkbox
                            checked={excludedUsers.includes(u.id)}
                            onCheckedChange={(checked) => {
                              setExcludedUsers(prev => 
                                checked ? [...prev, u.id] : prev.filter(id => id !== u.id)
                              );
                            }}
                          />
                          <span className="truncate">{u.name} ({u.role})</span>
                        </label>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateAnnouncement}>
                Send Announcement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Capacity */}
        <Card className="border border-border" data-testid="capacity-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Capacity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6 md:gap-8">
              <div>
                <p className="label-text mb-1">In Flow</p>
                <p className="text-xl md:text-2xl font-light text-blue-600" style={{ fontFamily: 'var(--font-sans)' }}>
                  {capacity.programs_in_flow || 0}
                </p>
              </div>
              <div>
                <p className="label-text mb-1">Active Programs</p>
                <p className="text-xl md:text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
                  {capacity.total_active_programs || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Tasks Checklist */}
        <TaskChecklist />
      </div>
    </div>
  );
};

export default TodayView;
